<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-if="store.currentUser" v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <UserAvatar :user="store.currentUser" />
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ group.name }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ item.label }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
        <p class="px-4 pt-1 text-[11px] text-[var(--va-secondary)] opacity-50">v1.4.0 · 2026-05-12</p>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useOAuthStore } from '@/stores/oauth'
import { useUserStore } from '@/stores/user-store'
import UserAvatar from '@/pages/users/widgets/UserAvatar.vue'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

type ProfileListItem = {
  name: string
  label: string
  to?: string
  href?: string
  icon: string
  action?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const isShown = ref(false)
const { push } = useRouter()
const authStore = useOAuthStore()
const store = useUserStore()
store.retrieveUserinfo()

const canSwitchMode = computed(() => ['organiser', 'admin', 'sa'].includes(store.role))

const options = computed<ProfileOptions[]>(() => {
  const groups: ProfileOptions[] = [
    {
      name: 'Account',
      separator: true,
      list: [
        { name: 'profile', label: 'Profile', to: 'preferences', icon: 'mso-account_circle' },
        { name: 'settings', label: 'Settings', to: 'settings', icon: 'mso-settings' },
      ],
    },
  ]

  if (canSwitchMode.value) {
    groups.push({
      name: '',
      separator: true,
      list: [
        store.mode === 'fan'
          ? {
              name: 'switchMode',
              label: 'Switch to Organiser mode',
              icon: 'mso-store',
              action: () => switchMode('organiser'),
            }
          : {
              name: 'switchMode',
              label: 'Switch to Fan mode',
              icon: 'mso-music_note',
              action: () => switchMode('fan'),
            },
      ],
    })
  }

  groups.push({
    name: '',
    separator: false,
    list: [{ name: 'logout', label: 'Logout', icon: 'mso-logout', action: handleLogout }],
  })

  return groups
})

const switchMode = (mode: 'fan' | 'organiser') => {
  store.setMode(mode)
  isShown.value = false
  push({ name: mode === 'organiser' ? 'managed-events' : 'events' })
}

const handleLogout = async () => {
  try {
    await authStore.signOut()
  } catch (error) {
    console.error('Fout bij uitloggen:', error)
  } finally {
    push({ name: 'login' })
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) {
    return { onClick: item.action }
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
